import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  IconButton,
  InputAdornment,
  Alert,
  CircularProgress,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar
} from '@mui/material';
import MuiAlert from '@mui/material/Alert';
import {
  Visibility,
  VisibilityOff,
  ContentCopy,
  Edit,
  Save,
  Cancel,
  CheckCircle,
  Delete
} from '@mui/icons-material';
import { useXeroApiKey } from '../../hooks/useXeroApiKey';
import { DateTime } from 'luxon';

const SnackbarAlert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const XeroApiKeyManagement = () => {
  const {
    xeroApiKey,
    isSet,
    lastUpdated,
    isLoading,
    error,
    updateXeroApiKey,
    deleteXeroApiKey,
    copyXeroApiKey,
  } = useXeroApiKey();

  const [showApiKey, setShowApiKey] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState('');
  const [showUpdateDialog, setShowUpdateDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'info'
  });

  const handleToggleVisibility = () => {
    setShowApiKey(!showApiKey);
  };

  const handleCopy = async () => {
    const success = await copyXeroApiKey();
    setNotification({
      open: true,
      message: success ? 'Xero API key copied to clipboard!' : 'Failed to copy Xero API key',
      severity: success ? 'success' : 'error'
    });
  };

  const handleStartEdit = () => {
    setEditValue(xeroApiKey);
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setEditValue('');
    setIsEditing(false);
  };

  const handleSaveEdit = () => {
    if (editValue.trim() !== xeroApiKey) {
      setShowUpdateDialog(true);
    } else {
      handleCancelEdit();
    }
  };

  const handleConfirmUpdate = async () => {
    setShowUpdateDialog(false);
    const success = await updateXeroApiKey(editValue.trim());
    
    if (success) {
      setNotification({
        open: true,
        message: 'Xero API key updated successfully!',
        severity: 'success'
      });
      setIsEditing(false);
      setEditValue('');
    } else {
      setNotification({
        open: true,
        message: error || 'Failed to update Xero API key',
        severity: 'error'
      });
    }
  };

  const handleDeleteClick = () => {
    setShowDeleteDialog(true);
  };

  const handleConfirmDelete = async () => {
    setShowDeleteDialog(false);
    const success = await deleteXeroApiKey();
    
    if (success) {
      setNotification({
        open: true,
        message: 'Xero API key deleted successfully!',
        severity: 'success'
      });
    } else {
      setNotification({
        open: true,
        message: error || 'Failed to delete Xero API key',
        severity: 'error'
      });
    }
  };

  const handleCloseNotification = () => {
    setNotification({ ...notification, open: false });
  };

  const formatLastUpdated = (dateString) => {
    if (!dateString) return 'Never';
    try {
      return DateTime.fromISO(dateString).toLocaleString(DateTime.DATETIME_MED);
    } catch {
      return 'Unknown';
    }
  };

  const displayValue = () => {
    if (isEditing) return editValue;
    if (!isSet) return '';
    if (showApiKey) return xeroApiKey;
    return '•'.repeat(Math.min(xeroApiKey.length, 40));
  };

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Typography variant="h6" gutterBottom>
        Xero API Key Management
      </Typography>
      
      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2">
          Your Xero API key enables integration with Xero accounting software. 
          This is optional and can be configured later when you're ready to integrate with Xero.
        </Typography>
      </Alert>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          API Key Status
        </Typography>
        <Chip
          icon={isSet ? <CheckCircle /> : undefined}
          label={isSet ? 'Configured' : 'Not Set'}
          color={isSet ? 'success' : 'default'}
          variant={isSet ? 'filled' : 'outlined'}
          sx={{ mb: 2 }}
        />
      </Box>

      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          Xero API Key
        </Typography>
        <TextField
          fullWidth
          value={displayValue()}
          onChange={(e) => setEditValue(e.target.value)}
          placeholder={isSet ? "Xero API key is set" : "Enter your Xero API key"}
          disabled={!isEditing || isLoading}
          type={showApiKey || isEditing ? 'text' : 'password'}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                {!isEditing && (
                  <>
                    {isSet && (
                      <IconButton
                        onClick={handleToggleVisibility}
                        disabled={isLoading}
                        size="small"
                      >
                        {showApiKey ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    )}
                    {isSet && showApiKey && (
                      <IconButton
                        onClick={handleCopy}
                        disabled={isLoading}
                        size="small"
                      >
                        <ContentCopy />
                      </IconButton>
                    )}
                    <IconButton
                      onClick={handleStartEdit}
                      disabled={isLoading}
                      size="small"
                    >
                      <Edit />
                    </IconButton>
                    {isSet && (
                      <IconButton
                        onClick={handleDeleteClick}
                        disabled={isLoading}
                        size="small"
                        color="error"
                      >
                        <Delete />
                      </IconButton>
                    )}
                  </>
                )}
                {isEditing && (
                  <>
                    <IconButton
                      onClick={handleSaveEdit}
                      disabled={isLoading}
                      size="small"
                      color="primary"
                    >
                      <Save />
                    </IconButton>
                    <IconButton
                      onClick={handleCancelEdit}
                      disabled={isLoading}
                      size="small"
                    >
                      <Cancel />
                    </IconButton>
                  </>
                )}
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {isSet && (
        <Typography variant="caption" color="text.secondary">
          Last updated: {formatLastUpdated(lastUpdated)}
        </Typography>
      )}

      {isLoading && (
        <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
          <CircularProgress size={20} sx={{ mr: 1 }} />
          <Typography variant="body2" color="text.secondary">
            {isEditing ? 'Updating Xero API key...' : 'Loading Xero API key...'}
          </Typography>
        </Box>
      )}

      {/* Update Confirmation Dialog */}
      <Dialog open={showUpdateDialog} onClose={() => setShowUpdateDialog(false)}>
        <DialogTitle>Update Xero API Key</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to update your Xero API key? This will affect Xero integration functionality.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowUpdateDialog(false)}>Cancel</Button>
          <Button onClick={handleConfirmUpdate} variant="contained" color="primary">
            Update
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onClose={() => setShowDeleteDialog(false)}>
        <DialogTitle>Delete Xero API Key</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete your Xero API key? This will disable Xero integration 
            and you'll need to re-enter the key to use Xero features again.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDeleteDialog(false)}>Cancel</Button>
          <Button onClick={handleConfirmDelete} variant="contained" color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notification Snackbar */}
      <Snackbar
        open={notification.open}
        autoHideDuration={4000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <SnackbarAlert onClose={handleCloseNotification} severity={notification.severity}>
          {notification.message}
        </SnackbarAlert>
      </Snackbar>
    </Paper>
  );
};

export default XeroApiKeyManagement; 